"use client";

import { useState, useEffect, useRef, useMemo } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { useRouter, usePathname } from 'next/navigation';
import { content } from '../content';
import { searchProducts } from '../utils/productSearch';
import { Category, Purpose, Product } from '../types';
import {
    IoCartOutline,
    IoSearchOutline,
    IoMenu,
    IoClose,
    IoChevronDown,
    IoNavigateOutline,
    IoBookOutline,
    IoChatbubblesOutline,
    IoGridOutline,
    IoCallOutline,
    IoSparklesOutline,
    IoLeafOutline,
    IoFitnessOutline,
    IoCashOutline,
    IoShieldCheckmarkOutline,
    IoHeartOutline,
    IoFlashOutline,
    IoPersonOutline,
    IoPersonCircleOutline
} from "react-icons/io5";
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useAllProducts } from '../context/ProductContext';

const getProductImage = (p: Product): string => {
    if (Array.isArray(p.image_url) && p.image_url.length > 0) return p.image_url[0];
    if (typeof p.image_url === 'string') return p.image_url;
    return '';
};

const getProductHref = (p: Product): string => {
    return p.slug === 'astroji-premium-aghori-evil-eye-locket-9c1efe'
        ? '/evil-eye/aghori-locket'
        : `/product/${p.slug}`;
};

// --- Data for Support dropdown ---
const supportLinks = [
    { name: 'Track Order', href: '/track-order', isExternal: false, icon: <IoNavigateOutline /> },
    { name: 'Product Usage', href: '/product-usage', icon: <IoBookOutline /> },
    { name: 'Contact Us', href: '/contact', isExternal: false, icon: <IoChatbubblesOutline /> }
];

interface NavbarProps {
    categories: Category[];
    purposes: Purpose[];
}

const Navbar = ({ categories, purposes }: NavbarProps) => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isSearchOpen, setIsSearchOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [isDesktopSearchFocused, setIsDesktopSearchFocused] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const { itemCount, isAnimating, toggleCartDrawer } = useCart();
    const { user, isLoggedIn } = useAuth();
    const { allProducts, loadProducts } = useAllProducts();
    const router = useRouter();
    const pathname = usePathname();
    const searchInputRef = useRef<HTMLInputElement>(null);
    const desktopSearchRef = useRef<HTMLDivElement>(null);

    // Map categories with icons/images
    const mappedCategories = categories.map(cat => {
        let Icon = IoGridOutline;
        const tag = cat.tag?.toLowerCase();
        if (tag?.includes('rudraksha')) Icon = IoLeafOutline;
        return {
            ...cat,
            href: `/products?byTag=${cat.tag}`,
            icon: Icon
        };
    });

    const mappedPurposes = purposes.map(p => {
        let Icon = IoSparklesOutline;
        const tag = p.tag?.toLowerCase();
        if (tag?.includes('money')) Icon = IoCashOutline;
        if (tag?.includes('health')) Icon = IoFitnessOutline;
        if (tag?.includes('protection')) Icon = IoShieldCheckmarkOutline;
        if (tag?.includes('love')) Icon = IoHeartOutline;
        if (tag?.includes('success')) Icon = IoFlashOutline;
        return {
            ...p,
            href: `/products?byTag=${p.tag}`,
            icon: Icon
        };
    });

    // Hide body scroll when mobile menu is open
    useEffect(() => {
        if (isMenuOpen || isSearchOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [isMenuOpen, isSearchOpen]);

    // Compute live search recommendations with weighted relevance
    const searchRecommendations = useMemo(() => {
        if (!searchQuery.trim() || allProducts.length === 0) return [];
        return searchProducts(allProducts, searchQuery).slice(0, 5);
    }, [searchQuery, allProducts]);

    const handleSelectProduct = (product: Product) => {
        setIsMenuOpen(false);
        setIsSearchOpen(false);
        setIsDesktopSearchFocused(false);
        setSearchQuery('');
        router.push(getProductHref(product));
    };

    // Close desktop search dropdown on click outside
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (desktopSearchRef.current && !desktopSearchRef.current.contains(e.target as Node)) {
                setIsDesktopSearchFocused(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleSearch = (e?: React.FormEvent) => {
        if (e) e.preventDefault();
        if (searchQuery.trim()) {
            router.push(`/products?query=${encodeURIComponent(searchQuery.trim())}`);
            setIsMenuOpen(false);
            setIsSearchOpen(false);
            setIsDesktopSearchFocused(false);
            setSearchQuery('');
        }
    };

    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth >= 1024) {
                setIsMenuOpen(false);
                setIsSearchOpen(false);
            }
        };

        const handleScroll = () => {
            setScrolled(window.scrollY > 20);
        };

        window.addEventListener('resize', handleResize);
        window.addEventListener('scroll', handleScroll);

        return () => {
            window.removeEventListener('resize', handleResize);
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    useEffect(() => {
        if (isSearchOpen) {
            loadProducts();
            setTimeout(() => {
                searchInputRef.current?.focus();
            }, 100);
        }
    }, [isSearchOpen, loadProducts]);

    return (
        <>
            <header
                className={`fixed top-0 left-0 w-full z-[1000] border-b border-gray-100/80 transition-all duration-200 ${scrolled
                    ? 'h-[58px] bg-white/95 shadow-[0_6px_20px_rgba(15,23,42,0.05)] backdrop-blur-md lg:h-[68px]'
                    : 'h-[60px] bg-white/90 backdrop-blur-sm lg:h-[72px]'
                    }`}
            >
                <div className="ui-container-nav justify-between relative">
                    {/* Logo - Aspect ratio prevents CLS while maintaining current look */}
                    <div className={`flex-shrink-0 transition-opacity duration-300 ${isSearchOpen ? 'opacity-0' : 'opacity-100'}`}>
                        <Link prefetch={false} href="/">
                            <Image
                                src={content.logoUrl}
                                alt="AstroJi Authentic Spiritual Products & Consultations"
                                width={180}
                                height={50}
                                priority
                                className="w-32 md:w-36 xl:w-38 h-auto"
                            />
                        </Link>
                    </div>

                    {/* --- Desktop Navigation (Dynamic) --- */}
                    <div className={`hidden lg:flex items-center justify-center flex-1 min-w-0 px-2 lg:px-4 xl:px-6 gap-x-3 lg:gap-x-4 xl:gap-x-5 2xl:gap-x-8 ${isSearchOpen ? 'opacity-0' : 'opacity-100'}`}>
                        {content.navbar.items.map((item: any, idx: number) => {
                            if (item.type === 'link') {
                                const isActive = pathname === item.href;
                                return (
                                    <Link
                                        key={idx}
                                        prefetch={false}
                                        href={item.href}
                                        className={`relative flex min-h-11 items-center whitespace-nowrap text-xs lg:text-[12px] xl:text-[13px] font-semibold uppercase tracking-wide transition-colors duration-200 after:absolute after:bottom-1 after:left-0 after:h-0.5 after:rounded-full after:bg-primary after:transition-all after:duration-200 ${isActive ? 'text-primary after:w-full' : 'text-gray-700 after:w-0 hover:text-primary hover:after:w-full'}`}
                                    >
                                        {item.text}
                                    </Link>
                                );
                            }

                            if (item.type === 'dynamic') {
                                const data = item.key === 'categories' ? mappedCategories : mappedPurposes;
                                return (
                                    <div key={idx} className="relative group h-full flex items-center">
                                        <button className="flex min-h-11 items-center gap-1 whitespace-nowrap text-xs lg:text-[12px] xl:text-[13px] font-semibold uppercase tracking-wide text-gray-700 hover:text-primary transition-colors duration-200">
                                            {item.text} <IoChevronDown className="transition-transform duration-300 group-hover:rotate-180" />
                                        </button>
                                        <div className="absolute top-full left-1/2 z-50 mt-1 w-56 -translate-x-1/2 scale-[0.98] overflow-hidden rounded-xl border border-gray-100 bg-white opacity-0 shadow-[0_12px_34px_rgba(15,23,42,0.10)] transition-all duration-200 invisible group-hover:visible group-hover:scale-100 group-hover:opacity-100">
                                            <div className="py-2">
                                                {data.map(sub => (
                                                    <Link
                                                        key={sub.name}
                                                        prefetch={false}
                                                        href={sub.href}
                                                        className="block px-5 py-2.5 text-sm text-gray-600 transition-colors hover:bg-orange-50/70 hover:text-primary"
                                                    >
                                                        {sub.name}
                                                    </Link>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                );
                            }

                            if (item.type === 'dropdown') {
                                return (
                                    <div key={idx} className="relative group h-full flex items-center">
                                        <button className="flex min-h-11 items-center gap-1 whitespace-nowrap text-xs lg:text-[12px] xl:text-[13px] font-semibold uppercase tracking-wide text-gray-700 hover:text-primary transition-colors duration-200">
                                            {item.text} <IoChevronDown className="transition-transform duration-300 group-hover:rotate-180" />
                                        </button>
                                        <div className="absolute top-full left-1/2 z-50 mt-1 w-56 -translate-x-1/2 scale-[0.98] overflow-hidden rounded-xl border border-gray-100 bg-white opacity-0 shadow-[0_12px_34px_rgba(15,23,42,0.10)] transition-all duration-200 invisible group-hover:visible group-hover:scale-100 group-hover:opacity-100">
                                            <div className="py-2">
                                                {item.sublinks.map((link: any) => (
                                                    link.isExternal ? (
                                                        <a
                                                            key={link.text}
                                                            href={link.href}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="block px-5 py-2.5 text-sm text-gray-600 transition-colors hover:bg-orange-50/70 hover:text-primary"
                                                        >
                                                            {link.text}
                                                        </a>
                                                    ) : (
                                                        <Link
                                                            key={link.text}
                                                            prefetch={false}
                                                            href={link.href}
                                                            className="block px-5 py-2.5 text-sm text-gray-600 transition-colors hover:bg-orange-50/70 hover:text-primary"
                                                        >
                                                            {link.text}
                                                        </Link>
                                                    )
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                );
                            }
                            return null;
                        })}
                    </div>

                    {/* Right Side Actions */}
                    <div className={`flex flex-shrink-0 items-center justify-end gap-1 sm:gap-2 xl:gap-3 ${isSearchOpen ? 'opacity-0' : 'opacity-100'}`}>
                        {/* Desktop Search */}
                        <div ref={desktopSearchRef} className="relative hidden 2xl:flex items-center">
                            <form onSubmit={handleSearch} className="relative flex items-center group">
                            <input
                                type="text"
                                placeholder="Search products..."
                                onFocus={() => {
                                    setIsDesktopSearchFocused(true);
                                    loadProducts();
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Escape') {
                                        setIsDesktopSearchFocused(false);
                                    }
                                }}
                                value={searchQuery}
                                onChange={(e) => {
                                    setSearchQuery(e.target.value);
                                    setIsDesktopSearchFocused(true);
                                    loadProducts();
                                }}
                                className="ui-input h-10 lg:h-11 w-48 2xl:w-64 focus:w-64 2xl:focus:w-80 rounded-full pl-9 pr-8 text-xs xl:text-sm transition-all duration-300"
                            />
                            <IoSearchOutline className="absolute left-3 text-gray-400 group-focus-within:text-primary transition-colors" size={18} />
                                {searchQuery && (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setSearchQuery('');
                                            setIsDesktopSearchFocused(false);
                                        }}
                                        className="absolute right-3 text-gray-400 hover:text-gray-600"
                                        aria-label="Clear search"
                                    >
                                        <IoClose size={15} />
                                    </button>
                                )}
                            </form>

                            {/* Desktop Recommendations Dropdown */}
                            {isDesktopSearchFocused && searchQuery.trim().length > 0 && (
                                <div className="absolute right-0 top-full mt-2 w-80 2xl:w-96 rounded-2xl border border-gray-100 bg-white p-3 shadow-2xl z-50">
                                    {searchRecommendations.length > 0 ? (
                                        <>
                                            <div className="flex items-center justify-between px-2 py-1 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
                                                <span>Recommendations</span>
                                                <span>{searchRecommendations.length} matches</span>
                                            </div>
                                            <div className="max-h-72 overflow-y-auto space-y-1 py-1">
                                                {searchRecommendations.map((product) => (
                                                    <button
                                                        key={product.id || product.slug}
                                                        type="button"
                                                        onClick={() => handleSelectProduct(product)}
                                                        className="flex w-full items-center gap-3 rounded-xl p-2 text-left transition-colors hover:bg-orange-50/80 group"
                                                    >
                                                        <div className="relative h-11 w-11 flex-shrink-0 overflow-hidden rounded-lg bg-gray-100 border border-gray-100">
                                                            {getProductImage(product) ? (
                                                                <Image
                                                                    src={getProductImage(product)}
                                                                    alt={product.name}
                                                                    fill
                                                                    sizes="44px"
                                                                    className="object-cover"
                                                                />
                                                            ) : (
                                                                <div className="flex h-full w-full items-center justify-center text-[10px] text-gray-400">
                                                                    AstroJi
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div className="min-w-0 flex-1">
                                                            <h4 className="text-xs font-semibold text-gray-900 line-clamp-1 group-hover:text-primary transition-colors">
                                                                {product.name}
                                                            </h4>
                                                            <div className="mt-0.5 flex items-center gap-1.5 text-[11px] text-gray-500">
                                                                {product.tags?.category_tags?.[0] && (
                                                                    <span className="capitalize">{product.tags.category_tags[0]}</span>
                                                                )}
                                                                {product.tags?.purpose_tags?.[0] && (
                                                                    <>
                                                                        <span>•</span>
                                                                        <span className="capitalize">{product.tags.purpose_tags[0]}</span>
                                                                    </>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <div className="text-right flex-shrink-0">
                                                            <div className="text-xs font-bold text-gray-900">₹{product.price}</div>
                                                            {product.compare_at_price && product.compare_at_price > product.price && (
                                                                <div className="text-[10px] text-gray-400 line-through">₹{product.compare_at_price}</div>
                                                            )}
                                                        </div>
                                                    </button>
                                                ))}
                                            </div>
                                            <button
                                                type="button"
                                                onClick={handleSearch}
                                                className="mt-2 flex w-full items-center justify-center gap-1.5 rounded-xl border border-orange-200/80 bg-orange-50/60 py-2 text-xs font-semibold text-primary transition-colors hover:bg-orange-100/80"
                                            >
                                                <span>View all results for &quot;{searchQuery.trim()}&quot;</span>
                                                <span>→</span>
                                            </button>
                                        </>
                                    ) : (
                                        <div className="p-3 text-center">
                                            <p className="text-xs text-gray-500">No instant recommendations</p>
                                            <button
                                                type="button"
                                                onClick={handleSearch}
                                                className="mt-1.5 inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
                                            >
                                                Search catalog for &quot;{searchQuery.trim()}&quot; →
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Search Icon Button for screens up to 2xl */}
                        <button onClick={() => setIsSearchOpen(true)} className="flex h-10 w-10 sm:h-11 sm:w-11 items-center justify-center rounded-full text-gray-700 transition-colors hover:bg-orange-50 hover:text-primary 2xl:hidden" aria-label="Open search">
                            <IoSearchOutline size={22} />
                        </button>

                        <Link prefetch={false} href={content.navbar.cta.href} className="hidden lg:block flex-shrink-0">
                            <button className="ui-button-accent min-h-10 lg:min-h-11 rounded-full px-3.5 sm:px-4 xl:px-6 py-1.5 lg:py-2 text-[11px] lg:text-xs xl:text-[13px] uppercase tracking-wide whitespace-nowrap">
                                <IoCallOutline size={16} /> <span className="hidden xl:inline">{content.navbar.cta.text}</span><span className="xl:hidden">Astrologer</span>
                            </button>
                        </Link>

                        {/* Desktop Login / Dashboard Button */}
                        <div className="hidden lg:block relative group h-full flex-shrink-0">
                            {isLoggedIn ? (
                                <Link prefetch={false} href="/dashboard" className="flex h-10 lg:h-11 items-center justify-center gap-1.5 rounded-full px-2.5 lg:px-3.5 text-xs xl:text-[13px] font-semibold text-gray-700 transition-colors hover:bg-orange-50 hover:text-primary whitespace-nowrap">
                                    <IoPersonCircleOutline size={20} className="text-primary" />
                                    {user?.name?.split(' ')[0] || 'Account'}
                                </Link>
                            ) : (
                                <Link prefetch={false} href="/login" className="flex h-10 lg:h-11 items-center justify-center gap-1.5 rounded-full px-2.5 lg:px-3.5 text-xs xl:text-[13px] font-semibold text-gray-700 transition-colors hover:bg-orange-50 hover:text-primary whitespace-nowrap">
                                    <IoPersonOutline size={19} />
                                    Login
                                </Link>
                            )}
                        </div>

                        <button onClick={toggleCartDrawer} className="group relative flex h-10 w-10 sm:h-11 sm:w-11 flex-shrink-0 items-center justify-center rounded-full text-gray-700 transition-colors hover:bg-orange-50 hover:text-primary" aria-label="Open cart">
                            <div className={`transition-transform duration-300 group-hover:scale-110 ${isAnimating ? 'animate-bump' : ''}`}>
                                <IoCartOutline size={24} />
                            </div>
                            {itemCount > 0 && (
                                <span className="absolute right-0 top-0 flex h-5 min-w-5 items-center justify-center rounded-full border-2 border-white bg-red-500 px-1 text-[10px] font-bold text-white">
                                    {itemCount}
                                </span>
                            )}
                        </button>

                        <button onClick={() => setIsMenuOpen(true)} className="flex h-10 w-10 sm:h-11 sm:w-11 flex-shrink-0 items-center justify-center rounded-full text-gray-800 transition-colors hover:bg-orange-50 hover:text-primary lg:hidden" aria-label="Open menu">
                            <IoMenu size={26} />
                        </button>
                    </div>
                </div>
            </header>

            {/* Search Modal */}
            <div className={`fixed inset-0 z-[9999] flex items-start justify-center bg-slate-950/50 px-4 pt-20 sm:items-center sm:pt-0 backdrop-blur-sm transition-all duration-200 ${isSearchOpen ? 'opacity-100 visible' : 'opacity-0 invisible pointer-events-none'}`}>
                <div className="absolute inset-0" onClick={() => setIsSearchOpen(false)} />

                <div className={`relative w-full max-w-xl bg-white p-5 sm:p-6 rounded-2xl shadow-2xl ${isSearchOpen ? 'translate-y-0 scale-100' : 'translate-y-6 scale-[0.98]'} transform transition-all duration-200`}>
                    <div className="flex justify-between items-center mb-5">
                        <h2 className="text-xl font-bold text-gray-900">Search AstroJi</h2>
                        <button
                            onClick={() => setIsSearchOpen(false)}
                            className="p-2 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors text-gray-600 hover:text-gray-900"
                            aria-label="Close search"
                        >
                            <IoClose size={20} />
                        </button>
                    </div>
                    <form onSubmit={handleSearch} className="relative group">
                        <input
                            ref={searchInputRef}
                            type="text"
                            placeholder="Search rudraksha, crystals, idols..."
                            value={searchQuery}
                            onChange={(e) => {
                                setSearchQuery(e.target.value);
                                loadProducts();
                            }}
                            className="ui-input h-12 rounded-xl pl-11 pr-24 text-base"
                        />
                        <IoSearchOutline className="absolute left-4 top-1/2 -translate-y-1/2 text-primary" size={21} />
                        <button
                            type="submit"
                            className="ui-button-accent absolute right-1.5 top-1/2 min-h-9 -translate-y-1/2 rounded-lg px-4 py-2"
                        >
                            Search
                        </button>
                    </form>
                    {searchQuery.trim().length > 0 ? (
                        <div className="mt-4">
                            {searchRecommendations.length > 0 ? (
                                <>
                                    <div className="mb-2 flex items-center justify-between px-1 text-xs font-semibold uppercase tracking-wider text-gray-400">
                                        <span>Recommended Products</span>
                                        <span>{searchRecommendations.length} matches</span>
                                    </div>
                                    <div className="max-h-72 sm:max-h-80 overflow-y-auto space-y-1.5 pr-1 divide-y divide-gray-50">
                                        {searchRecommendations.map((product) => (
                                            <button
                                                key={product.id || product.slug}
                                                type="button"
                                                onClick={() => handleSelectProduct(product)}
                                                className="flex w-full items-center gap-3 rounded-xl p-2 text-left transition-colors hover:bg-orange-50/80 active:bg-orange-100 group"
                                            >
                                                <div className="relative h-12 w-12 flex-shrink-0 overflow-hidden rounded-lg bg-gray-100 border border-gray-200/60">
                                                    {getProductImage(product) ? (
                                                        <Image
                                                            src={getProductImage(product)}
                                                            alt={product.name}
                                                            fill
                                                            sizes="48px"
                                                            className="object-cover"
                                                        />
                                                    ) : (
                                                        <div className="flex h-full w-full items-center justify-center text-[10px] text-gray-400">
                                                            AstroJi
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="min-w-0 flex-1">
                                                    <h4 className="text-sm font-medium text-gray-900 line-clamp-1 group-hover:text-primary transition-colors">
                                                        {product.name}
                                                    </h4>
                                                    <div className="mt-0.5 flex items-center gap-2 text-xs text-gray-500">
                                                        {product.tags?.category_tags?.[0] && (
                                                            <span className="capitalize">{product.tags.category_tags[0]}</span>
                                                        )}
                                                        {product.tags?.purpose_tags?.[0] && (
                                                            <>
                                                                <span>•</span>
                                                                <span className="capitalize">{product.tags.purpose_tags[0]}</span>
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="text-right flex-shrink-0">
                                                    <div className="text-sm font-bold text-gray-900">₹{product.price}</div>
                                                    {product.compare_at_price && product.compare_at_price > product.price && (
                                                        <div className="text-xs text-gray-400 line-through">₹{product.compare_at_price}</div>
                                                    )}
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                    <button
                                        type="button"
                                        onClick={handleSearch}
                                        className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl border border-orange-200/80 bg-orange-50/70 py-2.5 text-xs font-semibold text-primary transition-colors hover:bg-orange-100"
                                    >
                                        <span>View all results for &quot;{searchQuery.trim()}&quot;</span>
                                        <span>→</span>
                                    </button>
                                </>
                            ) : (
                                <div className="rounded-xl border border-dashed border-gray-200 py-6 text-center">
                                    <p className="text-sm font-medium text-gray-600">No instant recommendations found</p>
                                    <p className="mt-1 text-xs text-gray-400">Press Search to explore all items in our catalog</p>
                                    <button
                                        type="button"
                                        onClick={handleSearch}
                                        className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
                                    >
                                        Search catalog for &quot;{searchQuery.trim()}&quot; →
                                    </button>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="mt-4 flex flex-wrap items-center gap-2">
                        <span className="text-xs font-semibold uppercase tracking-wide text-gray-400">Popular</span>
                        {['Rudraksha', 'Bracelets', 'Money', 'Health'].map(tag => (
                            <button
                                key={tag}
                                onClick={() => {
                                    setSearchQuery(tag);
                                    router.push(`/products?query=${encodeURIComponent(tag)}`);
                                    setIsSearchOpen(false);
                                }}
                                className="ui-pill hover:border-primary/40 hover:text-primary"
                            >
                                {tag}
                            </button>
                        ))}
                    </div>
                )}
                </div>
            </div>

            {/* --- Mobile Menu Overlay --- */}
            {isMenuOpen && (
                <div className="fixed inset-0 z-[9998] lg:hidden transition-all duration-300 ease-in-out">
                    <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setIsMenuOpen(false)} />
                    <div className="relative z-10 flex h-full w-[88%] max-w-sm flex-col bg-white shadow-[18px_0_42px_rgba(15,23,42,0.18)]" onClick={(e) => e.stopPropagation()}>
                        <div className="flex h-[60px] items-center justify-between border-b border-gray-100 bg-white px-5">
                            <Link prefetch={false} href="/" onClick={() => setIsMenuOpen(false)}>
                                <Image src={content.logoUrl} alt="Astroji Logo" width={180} height={50} className="h-auto w-24" />
                            </Link>
                            <button onClick={() => setIsMenuOpen(false)} className="flex h-10 w-10 items-center justify-center rounded-full text-gray-500 transition-colors hover:bg-gray-50 hover:text-red-500" aria-label="Close menu">
                                <IoClose size={24} />
                            </button>
                        </div>

                        <div className="flex-1 overflow-y-auto py-4 pb-20">
                            <div className="mb-4 px-5 space-y-2">
                                <Link prefetch={false} href={content.navbar.cta.href} onClick={() => setIsMenuOpen(false)}>
                                    <button className="ui-button-accent w-full rounded-lg">
                                        <IoCallOutline /> {content.navbar.cta.text}
                                    </button>
                                </Link>

                                {/* Mobile Login / Dashboard Button */}
                                {isLoggedIn ? (
                                    <Link prefetch={false} href="/dashboard" onClick={() => setIsMenuOpen(false)} className="block w-full">
                                        <button className="ui-button-soft w-full rounded-lg justify-start gap-3">
                                            <IoPersonCircleOutline size={20} className="text-primary" /> My Dashboard
                                        </button>
                                    </Link>
                                ) : (
                                    <Link prefetch={false} href="/login" onClick={() => setIsMenuOpen(false)} className="block w-full">
                                        <button className="ui-button-soft w-full rounded-lg justify-start gap-3">
                                            <IoPersonOutline size={20} /> Login / Sign Up
                                        </button>
                                    </Link>
                                )}
                            </div>

                            <div className="space-y-1 px-5">
                                {content.navbar.items.map((item: any, idx: number) => {
                                    if (item.type === 'link') {
                                        const isActive = pathname === item.href;
                                        return (
                                            <Link
                                                key={idx}
                                                prefetch={false}
                                                href={item.href}
                                                onClick={() => setIsMenuOpen(false)}
                                                className={`flex min-h-11 items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-semibold uppercase tracking-wide transition-colors ${isActive ? 'bg-orange-50 text-primary' : 'text-gray-700 hover:bg-gray-50'}`}
                                            >
                                                <IoGridOutline size={18} /> {item.text}
                                            </Link>
                                        );
                                    }

                                    if (item.type === 'dynamic') {
                                        const data = item.key === 'categories' ? mappedCategories : mappedPurposes;
                                        const tagLabel = item.key === 'categories' ? 'Explore Categories' : 'By Purpose';

                                        return (
                                            <div key={idx} className="my-5 border-t border-gray-100 px-1 pt-5">
                                                <p className="mb-4 px-2 text-xs font-bold uppercase tracking-widest text-gray-400">{tagLabel}</p>
                                                <div className="grid grid-cols-3 gap-x-3 gap-y-5">
                                                    {data.map(sub => (
                                                        <Link
                                                            key={sub.id}
                                                            prefetch={false}
                                                            href={sub.href}
                                                            onClick={() => setIsMenuOpen(false)}
                                                            className="flex flex-col items-center gap-2 group"
                                                        >
                                                            <div className={`flex h-14 w-14 items-center justify-center overflow-hidden rounded-xl border border-gray-100 transition-all group-active:scale-95 group-hover:border-primary/30 ${item.key === 'purposes' ? 'bg-orange-50/60' : 'bg-white'}`}>
                                                                {(sub as any).image_url ? (
                                                                    <Image src={(sub as any).image_url} alt={sub.name} width={56} height={56} className="h-full w-full rounded-lg object-cover p-1" />
                                                                ) : (
                                                                    <sub.icon className="text-primary text-2xl" />
                                                                )}
                                                            </div>
                                                            <span className="text-center text-[11px] font-semibold capitalize leading-tight text-accent">{sub.name}</span>
                                                        </Link>
                                                    ))}
                                                </div>
                                            </div>
                                        );
                                    }

                                    if (item.type === 'dropdown') {
                                        return (
                                            <div key={idx} className="my-4 border-t border-gray-100 pt-4">
                                                <p className="mb-2 px-3 text-xs font-bold uppercase tracking-wider text-gray-400">{item.text}</p>
                                                <div className="space-y-1">
                                                    {item.sublinks.map((link: any, sIdx: number) => {
                                                        const Icon = supportLinks.find(sl => sl.name === link.text)?.icon || <IoSparklesOutline />;
                                                        return link.isExternal ? (
                                                            <a
                                                                key={sIdx}
                                                                href={link.href}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="flex min-h-11 items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50 hover:text-primary"
                                                            >
                                                                {Icon} {link.text}
                                                            </a>
                                                        ) : (
                                                            <Link
                                                                key={sIdx}
                                                                prefetch={false}
                                                                href={link.href}
                                                                onClick={() => setIsMenuOpen(false)}
                                                                className="flex min-h-11 items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50 hover:text-primary"
                                                            >
                                                                {Icon} {link.text}
                                                            </Link>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        );
                                    }
                                    return null;
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default Navbar;
